import React, { useState, useEffect, useContext } from "react";
import { Form, Radio, Input, Card, Row, Col, DatePicker, Button } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import API from "api/endpoints";
import { CorrectiveActionContext } from "./CorrectiveActionContext";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "Common/Footer";
import moment from "moment";
import axiosInstance from "api/axiosInstance";

function CorrectiveActionForm() {
  const { id, complainantEmployeeId } = useParams();
  const navigate = useNavigate();
  const [complaintDetails, setComplaintDetails] = useState([]);
  const { setIsSubmitted } = useContext(CorrectiveActionContext);
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const complaintId = id;

  const [formFields, setFormFields] = useState({
    departmentName: "",
    whenDate: "",
    improvement: "",
    associateComments: "",
    summarySession: "",
    leaderName: "",
    date: "",
  });

  const [warned, setWarned] = useState("");

  const typeOfWarningMap = {
    "Verbal Warning": 0,
    "Written Warning": 1,
    "Final Written Warning": 2,
  };

  useEffect(() => {
    fetchComplaintDetail();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormFields({ ...formFields, [name]: value });
  };

  const handleWarnedChange = (value) => {
    setWarned(value);
  };

  const handleSubmit = async () => {
    try {
      if (!complaintId) throw new Error("Complaint ID is missing.");

      await form.validateFields();
      if (complaintDetails.length > 0) {
        const data = {
          complaintId: parseInt(complaintId, 10),
          typeOfWarning: typeOfWarningMap[form.getFieldValue("typeOfWarning")],
          warnedPreviously: warned === "Yes",
          improvement: formFields.improvement,
          associateComments: formFields.associateComments,
          summarySession: formFields.summarySession,
          leaderName: formFields.leaderName,
          date: formFields.date,
        };

        setIsLoading(true);
        await axiosInstance.post(API.correctiveAction.create, data);
        setIsSubmitted(true);
        localStorage.setItem(`complaint_${id}_submitted`, "true");
        navigate(`/complaintDetail/${id}/${complainantEmployeeId}`);
      }
    } catch (error) {
      console.error("Validation or submission error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  async function fetchComplaintDetail() {
    try {
      setLoading(true);
      if (complaintId) {
        const response = await axiosInstance.get(API.complaint.getComplaint(complaintId));
        const responseData = response.data;
        setComplaintDetails(Array.isArray(responseData) ? responseData : [responseData]);
      } else {
        console.error("ComplaintId not found");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }

  const { TextArea } = Input;

  return (
    <DashboardLayout>
      <div className="wrapper d-flex flex-column min-vh-100 bg-light">
        <br />
        <Card loading={loading} title="Associate Corrective Action" className="card-professional">
          <Form form={form} layout="vertical">
            {complaintDetails.map((com) => (
              <>
                {/* ===== Predefined Complaint Information ===== */}
                <div style={{ marginBottom: "20px" }}>
                  <Row gutter={[16, 8]}>
                    <Col span={12}>
                      <p>
                        <strong>Associate Name:</strong> {com.complainantFirstName}{" "}
                        {com.complainantLastName}
                      </p>
                    </Col>
                    <Col span={12}>
                      <p>
                        <strong>Associate Email:</strong> {com.complainantEmail}
                      </p>
                    </Col>
                    <Col span={12}>
                      <p>
                        <strong>Department:</strong> {com.departmentName}
                      </p>
                    </Col>
                    <Col span={12}>
                      <p>
                        <strong>Complaint Type:</strong> {com.complaintTypeName}
                      </p>
                    </Col>
                    <Col span={12}>
                      <p>
                        <strong>Complaint Against:</strong> {com.complaintAgainstFirstName}{" "}
                        {com.complaintAgainstLastName}
                      </p>
                    </Col>
                    <Col span={12}>
                      <p>
                        <strong>Date of Incident:</strong>{" "}
                        {com.createdOn ? moment(com.createdOn).format("YYYY-MM-DD HH:mm") : "N/A"}
                      </p>
                    </Col>
                    <Col span={24}>
                      <p>
                        <strong>Details of Incident:</strong> {com.detailOfIncident}
                      </p>
                    </Col>
                  </Row>
                </div>

                <hr />

                {/* ===== Form Fields ===== */}
                <Form.Item
                  label="Type of Warning"
                  name="typeOfWarning"
                  rules={[{ required: true, message: "Type of Warning is required" }]}
                >
                  <Radio.Group>
                    <Radio value="Verbal Warning">Verbal Warning</Radio>
                    <Radio value="Written Warning">Written Warning</Radio>
                    <Radio value="Final Written Warning">Final Written Warning</Radio>
                  </Radio.Group>
                </Form.Item>

                <Form.Item
                  label="Has the associate been warned of a similar incident?"
                  name="warned"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Radio.Group onChange={(e) => handleWarnedChange(e.target.value)}>
                    <Radio value="Yes">Yes</Radio>
                    <Radio value="No">No</Radio>
                  </Radio.Group>
                </Form.Item>

                {warned === "Yes" && (
                  <>
                    <Form.Item
                      label="If yes, how and when?"
                      name="yes"
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <Radio.Group>
                        <Radio value="Verbal">Verbal</Radio>
                        <Radio value="Written">Written</Radio>
                      </Radio.Group>
                    </Form.Item>

                    <Col span={12}>
                      <Form.Item
                        label="When (dates):"
                        name="whenDate"
                        rules={[{ required: true, message: "Required" }]}
                      >
                        <DatePicker
                          value={formFields.whenDate ? moment(formFields.whenDate) : null}
                          onChange={(date, dateString) =>
                            handleInputChange({ target: { name: "whenDate", value: dateString } })
                          }
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </Col>
                  </>
                )}

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Action Plan(s) for Improvement"
                      name="improvement"
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <TextArea
                        rows={3}
                        name="improvement"
                        value={formFields.improvement}
                        onChange={handleInputChange}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Associate’s Comments"
                      name="associateComments"
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <TextArea
                        rows={3}
                        name="associateComments"
                        value={formFields.associateComments}
                        onChange={handleInputChange}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Summary of counseling session"
                      name="summarySession"
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <TextArea
                        rows={3}
                        name="summarySession"
                        value={formFields.summarySession}
                        onChange={handleInputChange}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <h6>
                  Further Violations May Result In Further Disciplinary Action, Up To and Including
                  Termination.
                </h6>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Leader’s Name"
                      name="leaderName"
                      rules={[{ required: true, message: "Leader's name is required" }]}
                    >
                      <Input
                        name="leaderName"
                        value={formFields.leaderName}
                        onChange={handleInputChange}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Date"
                      name="date"
                      rules={[{ required: true, message: "Date is required" }]}
                    >
                      <DatePicker
                        value={formFields.date ? moment(formFields.date) : null}
                        onChange={(date, dateString) =>
                          handleInputChange({ target: { name: "date", value: dateString } })
                        }
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row justify="end">
                  <Col>
                    <Button className="btn-primary" loading={isLoading} onClick={handleSubmit}>
                      Submit
                    </Button>
                  </Col>
                </Row>
              </>
            ))}
          </Form>
        </Card>
        <Footer />
      </div>
    </DashboardLayout>
  );
}

export default CorrectiveActionForm;
